import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, X } from 'lucide-react';
import api from '../../services/api';
import BookingModal from './BookingModal';

interface AlertItem {
  itinerary_id: number;
  activity_name: string;
  activity_date: string | null;
  guest_name: string | null;
}

interface Activity {
  id: number;
  activity_date: string | null;
  itinerary_id: number;
  guest_name: string;
  activity_name: string;
  item_type: string;
  item_subtype: string;
  status: string;
  vendor_name: string;
  day_number: number;
  estimated_amount: number;
  adults: number;
  kids: number;
  travel_date: string | null;
  quotation_number: string | null;
  verification_status: string | null;
}

interface Filters {
  quotation_id: string;
  itinerary_id: string;
  lead_guest_name: string;
  travel_date_from: string;
  travel_date_to: string;
  activity_name: string;
  item_type: string;
  status: string;
}

const STATUS_COLORS: Record<string, string> = {
  NOT_BOOKED: 'bg-gray-100 text-gray-700',
  BOOKED: 'bg-blue-100 text-blue-800',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-800',
  PAYMENT_COMPLETED: 'bg-green-100 text-green-800',
  CANCELED_WITH_FEE: 'bg-red-100 text-red-800',
};

const TYPE_LABELS: Record<string, string> = {
  prebooked: 'Prebooked',
  driver_paid: 'Driver Paid',
};

type SortKey = 'activity_date' | 'itinerary_id' | 'guest_name' | 'activity_name' | 'item_type' | 'status';

const BookerAllyPage: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [kecakAlerts, setKecakAlerts] = useState<AlertItem[]>([]);
  const [alohaAlerts, setAlohaAlerts] = useState<AlertItem[]>([]);
  const [showKecakDialog, setShowKecakDialog] = useState(false);
  const [selectedItineraryId, setSelectedItineraryId] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'activity_date', direction: 'asc' });
  const [filters, setFilters] = useState<Filters>(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    return {
      quotation_id: '',
      itinerary_id: '',
      lead_guest_name: '',
      travel_date_from: today.toISOString().split('T')[0],
      travel_date_to: nextWeek.toISOString().split('T')[0],
      activity_name: '',
      item_type: '',
      status: '',
    };
  });

  const loadActivities = useCallback(async (p: number = page) => {
    try {
      setLoading(true);
      const payload: any = { filters: {} };
      if (filters.quotation_id) payload.filters.quotation_id = filters.quotation_id;
      if (filters.itinerary_id) payload.filters.itinerary_id = filters.itinerary_id;
      if (filters.lead_guest_name) payload.filters.lead_guest_name = filters.lead_guest_name;
      if (filters.travel_date_from) payload.filters.travel_date_from = filters.travel_date_from;
      if (filters.travel_date_to) payload.filters.travel_date_to = filters.travel_date_to;
      if (filters.activity_name) payload.filters.activity_name = filters.activity_name;
      if (filters.item_type) payload.filters.item_type = filters.item_type;
      if (filters.status) payload.filters.status = filters.status;

      const res = await api.post(`/booker-ally/activities?page=${p}&limit=50`, payload.filters);
      if (res.data.success) {
        setActivities(res.data.data);
        setTotalPages(res.data.pagination.pages);
        setTotal(res.data.pagination.total);
      }
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadActivities(page);
  }, [page]);

  useEffect(() => {
    const loadAlerts = async () => {
      try {
        const res = await api.get('/booker-ally/alerts');
        if (res.data.success) {
          if (res.data.kecak_dance?.length) {
            setKecakAlerts(res.data.kecak_dance);
            setShowKecakDialog(true);
          }
          if (res.data.alas_harum_aloha?.length) {
            setAlohaAlerts(res.data.alas_harum_aloha);
          }
        }
      } catch (err) {
        console.error('Error loading alerts:', err);
      }
    };
    loadAlerts();
  }, []);

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB');
  };

  const handleSearch = () => {
    setPage(1);
    loadActivities(1);
  };

  const handleReset = () => {
    const empty: Filters = {
      quotation_id: '', itinerary_id: '', lead_guest_name: '',
      travel_date_from: '', travel_date_to: '', activity_name: '', item_type: '', status: '',
    };
    setFilters(empty);
    setPage(1);
    // load with empty filters
    setTimeout(() => loadActivities(1), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const hasFilters = Object.values(filters).some(v => v !== '');

  const today = new Date().toISOString().split('T')[0];

  const isPastOrToday = (d: string | null) => {
    if (!d) return false;
    return d <= today;
  };

  const handleVerify = async (item: Activity, status: 'VERIFIED' | 'NO_SHOW') => {
    const label = status === 'VERIFIED' ? 'Verified' : 'No Show';
    const current = item.verification_status;
    // Toggle off if clicking the same status
    const newStatus = current === status ? null : status;
    const msg = newStatus
      ? `Mark this activity as "${label}"?\n\n${item.activity_name} — WH_${item.itinerary_id}`
      : `Remove "${current === 'VERIFIED' ? 'Verified' : 'No Show'}" status?\n\n${item.activity_name} — WH_${item.itinerary_id}`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/booker-ally/verify/${item.id}`, { verification_status: newStatus || 'UNSET' });
      setActivities(prev => prev.map(a => a.id === item.id ? { ...a, verification_status: newStatus } : a));
    } catch (err) {
      console.error('Error updating verification:', err);
    }
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedActivities = [...activities].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const key = sortConfig.key;
    const valA = a[key];
    const valB = b[key];
    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;
    if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * dir;
    return String(valA).localeCompare(String(valB)) * dir;
  });

  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortConfig.key !== col) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">BookerAlly</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Activity booking overview — track what needs to be booked across all trips
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3" onKeyDown={handleKeyDown}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quotation ID</label>
              <input
                type="text"
                placeholder="e.g. Q-0012"
                value={filters.quotation_id}
                onChange={e => setFilters({ ...filters, quotation_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Itinerary ID</label>
              <input
                type="text"
                placeholder="e.g. 9734"
                value={filters.itinerary_id}
                onChange={e => setFilters({ ...filters, itinerary_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Lead Guest Name</label>
              <input
                type="text"
                placeholder="Guest name"
                value={filters.lead_guest_name}
                onChange={e => setFilters({ ...filters, lead_guest_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.item_type}
                onChange={e => setFilters({ ...filters, item_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="prebooked">Prebooked</option>
                <option value="driver_paid">Driver Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="NOT_BOOKED">Not Booked</option>
                <option value="BOOKED">Booked</option>
                <option value="PENDING_PAYMENT">Pending Payment</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
                <option value="PAYMENT_COMPLETED">Payment Completed</option>
                <option value="CANCELED_WITH_FEE">Cancelled with Fee</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3" onKeyDown={handleKeyDown}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Activity Date From</label>
              <input
                type="date"
                value={filters.travel_date_from}
                onChange={e => setFilters({ ...filters, travel_date_from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Activity Date To</label>
              <input
                type="date"
                value={filters.travel_date_to}
                onChange={e => setFilters({ ...filters, travel_date_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Activity Name</label>
              <input
                type="text"
                placeholder="e.g. Watersports"
                value={filters.activity_name}
                onChange={e => setFilters({ ...filters, activity_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
              >
                <Search className="w-4 h-4" /> Search
              </button>
              {hasFilters && (
                <button
                  onClick={handleReset}
                  className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Alas Harum / Aloha warning banner */}
        {alohaAlerts.length > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Alas Harum / Aloha — Not booked in next 5 days</span>
            </div>
            <div className="space-y-1">
              {alohaAlerts.map((a, i) => (
                <div key={i} className="text-sm text-amber-700">
                  WH_{a.itinerary_id} · {a.activity_name} · {formatDate(a.activity_date)}{a.guest_name ? ` · ${a.guest_name}` : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results count */}
        <div className="mb-3 text-sm text-gray-500">
          {total} activities found{totalPages > 1 && ` · Page ${page} of ${totalPages}`}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Mobile cards */}
          <div className="block sm:hidden divide-y divide-gray-200">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : activities.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No activities found</div>
            ) : (
              activities.map((a, i) => (
                <div key={i} className="p-4 space-y-2 cursor-pointer" onClick={() => setSelectedItineraryId(a.itinerary_id)}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{a.activity_name}</div>
                      <div className="text-xs text-gray-500">{a.vendor_name || '—'}</div>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-700'}`}>
                      {a.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{formatDate(a.activity_date)}</span>
                    <a
                      href={`/booking`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      WH_{a.itinerary_id} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{a.guest_name || '—'}</span>
                    <span className="capitalize">{TYPE_LABELS[a.item_type] || a.item_type}</span>
                  </div>
                  {isPastOrToday(a.activity_date) && (
                    <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleVerify(a, 'VERIFIED')}
                        className={`px-2 py-1 text-xs font-medium rounded border ${a.verification_status === 'VERIFIED' ? 'bg-green-600 text-white border-green-600' : 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'}`}
                      >
                        ✓ Verified
                      </button>
                      <button
                        onClick={() => handleVerify(a, 'NO_SHOW')}
                        className={`px-2 py-1 text-xs font-medium rounded border ${a.verification_status === 'NO_SHOW' ? 'bg-red-600 text-white border-red-600' : 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'}`}
                      >
                        ✗ No Show
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { key: 'activity_date' as SortKey, label: 'Date' },
                    { key: 'itinerary_id' as SortKey, label: 'Itinerary' },
                    { key: 'guest_name' as SortKey, label: 'Guest' },
                    { key: 'activity_name' as SortKey, label: 'Activity' },
                    { key: 'item_type' as SortKey, label: 'Type' },
                    { key: 'status' as SortKey, label: 'Status' },
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        <SortIcon col={col.key} />
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verify</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </td>
                  </tr>
                ) : activities.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">No activities found</td>
                  </tr>
                ) : (
                  sortedActivities.map((a, i) => (
                    <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedItineraryId(a.itinerary_id)}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(a.activity_date)}
                        {a.day_number ? <div className="text-xs text-gray-400">Day {a.day_number}</div> : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <a
                          href={`/booking`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1"
                        >
                          WH_{a.itinerary_id} <ExternalLink className="w-3 h-3" />
                        </a>
                        {a.quotation_number && (
                          <div className="text-xs text-gray-400">{a.quotation_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-[180px] truncate" title={a.guest_name || ''}>
                        {a.guest_name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{a.activity_name}</div>
                        {a.vendor_name && <div className="text-xs text-gray-500">{a.vendor_name}</div>}
                        {(a.adults > 0 || a.kids > 0) && (
                          <div className="text-xs text-gray-400">{a.adults} adults{a.kids > 0 ? ` + ${a.kids} kids` : ''}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm capitalize">{TYPE_LABELS[a.item_type] || a.item_type}</span>
                        {a.item_subtype && <div className="text-xs text-gray-400 capitalize">{a.item_subtype}</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-700'}`}>
                          {a.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {isPastOrToday(a.activity_date) ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleVerify(a, 'VERIFIED')}
                              className={`px-2 py-1 text-xs font-medium rounded border ${a.verification_status === 'VERIFIED' ? 'bg-green-600 text-white border-green-600' : 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'}`}
                            >
                              ✓ Verified
                            </button>
                            <button
                              onClick={() => handleVerify(a, 'NO_SHOW')}
                              className={`px-2 py-1 text-xs font-medium rounded border ${a.verification_status === 'NO_SHOW' ? 'bg-red-600 text-white border-red-600' : 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'}`}
                            >
                              ✗ No Show
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {/* Booking Modal */}
      {selectedItineraryId && (
        <BookingModal
          itineraryId={selectedItineraryId}
          onClose={() => setSelectedItineraryId(null)}
        />
      )}

      {/* Kecak Dance Error Dialog */}
      {showKecakDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <h2 className="text-lg font-bold text-red-700">Kecak Dance — Not Booked!</h2>
              </div>
              <button onClick={() => setShowKecakDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">The following Kecak Dance activities are not booked within the next 30 days:</p>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {kecakAlerts.map((a, i) => (
                <div key={i} className="flex justify-between items-center bg-red-50 rounded-lg px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium text-red-800">WH_{a.itinerary_id}</span>
                    <span className="text-red-600 ml-2">{a.activity_name}</span>
                    {a.guest_name && <span className="text-red-500 ml-2">· {a.guest_name}</span>}
                  </div>
                  <span className="text-red-700 font-medium">{formatDate(a.activity_date)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowKecakDialog(false)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookerAllyPage;
